import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';

import { listAllRooms } from 'services/nh-room-dashboard';
import {
  useDashboardTheme, ACCENT_COLOR, accentBarSx, cardSx,
  panelHeaderSx, countBadgeSx, tableHeadRowSx, tableRowSx, scrollbarSx, fmt, fmtTimeMedium
} from 'layouts/nh-room-dashboard/utils';

const statusColors = {
  ACTIVE: 'success',
  EXPIRED: 'warning',
  ERROR: 'error',
  INACTIVE: 'default'
};

function fmtBidTime(ts) {
  if (!ts || ts <= 0) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function NHRoomList() {
  const navigate = useNavigate();
  const t = useDashboardTheme();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAllRooms();
      setRooms(data || []);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRooms();
    const timer = setInterval(fetchRooms, 15000);
    return () => clearInterval(timer);
  }, [fetchRooms]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        {/* Header Card */}
        <Card sx={{ ...cardSx, mb: 3 }}>
          <MDBox sx={accentBarSx} />
          <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
            <MDBox display='flex' justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap='wrap' gap={2}>
              <MDBox display='flex' alignItems='center' gap={{ xs: 1.5, sm: 2 }}>
                <MDBox
                  sx={{
                    width: { xs: '40px', sm: '48px' },
                    height: { xs: '40px', sm: '48px' },
                    borderRadius: '14px',
                    background: ACCENT_COLOR,
                    boxShadow: '0 4px 12px rgba(226,0,27,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Icon sx={{ color: 'white !important', fontSize: '1.5rem !important' }}>dns</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant='h5' fontWeight='bold' sx={{ letterSpacing: '-0.01em', lineHeight: 1.2, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                    Crypto Express — Active Rooms
                  </MDTypography>
                  <MDTypography variant='body2' color='text' sx={{ opacity: 0.6, fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>
                    Live mining rooms with auto-refresh
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDButton
                variant='outlined'
                color='info'
                size='small'
                onClick={fetchRooms}
                disabled={loading}
                sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.72rem' }}
              >
                <Icon sx={{ mr: 0.5, fontSize: '1rem !important' }}>refresh</Icon>
                Refresh
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        {/* Rooms Table */}
        <MDBox mb={3}>
          <MDBox display='flex' alignItems='center' gap={1.25} mb={2.5}>
            <MDBox sx={{ width: '3px', height: '18px', borderRadius: '2px', background: ACCENT_COLOR, flexShrink: 0 }} />
            <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', color: t.primaryText, fontSize: '0.95rem' }}>
              Active Rooms
            </MDTypography>
            <MDBox sx={{ flex: 1, height: '1px', background: t.divider, display: { xs: 'none', sm: 'block' } }} />
          </MDBox>

          <Card sx={cardSx}>
            <MDBox sx={panelHeaderSx(t)}>
              <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
                Room List
              </MDTypography>
              <MDBox sx={countBadgeSx(t)}>
                <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: t.tableHeaderColor, letterSpacing: '0.04em' }}>
                  {rooms.length} room{rooms.length !== 1 ? 's' : ''}
                </MDTypography>
              </MDBox>
            </MDBox>

            {loading && rooms.length === 0 ? (
              <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} />
              </MDBox>
            ) : rooms.length === 0 ? (
              <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={1}>
                <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>
                  No active rooms found. Start a game to see rooms here.
                </MDTypography>
              </MDBox>
            ) : (
              <TableContainer sx={scrollbarSx(t)}>
                <Table size='small' stickyHeader>
                  <TableHead>
                    <TableRow sx={tableHeadRowSx(t)}>
                      <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
                      <TableCell>Room ID</TableCell>
                      <TableCell>Bet Limit</TableCell>
                      <TableCell>Coins</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell align='right'>Players</TableCell>
                      <TableCell align='right'>Wagered ($)</TableCell>
                      <TableCell align='right'>P&L ($)</TableCell>
                      <TableCell>Last Bid</TableCell>
                      <TableCell align='center'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rooms.map((room, index) => {
                      const isTop = index === 0;
                      return (
                        <TableRow
                          key={room.roomId}
                          sx={{ ...tableRowSx(t, { index, isTop }), cursor: 'pointer' }}
                          onClick={() => navigate(`/nh-room/${room.roomId}`)}
                        >
                          <TableCell sx={{ width: 28, pr: 0 }}>
                            <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? t.rankTopColor : t.rankColor, fontWeight: isTop ? 700 : 400 }}>
                              {index + 1}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' fontWeight={isTop ? 'bold' : 'medium'} sx={{ fontSize: '0.78rem', color: t.primaryText, fontFamily: 'monospace' }}>
                              {room.roomId.length > 20 ? `${room.roomId.slice(0, 10)}...${room.roomId.slice(-8)}` : room.roomId}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText, fontFamily: 'monospace' }}>
                              {room.betLimitId.slice(0, 8)}…
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            {(room.coins?.length > 0) ? room.coins.map((c) => (
                              <Chip key={c} label={c} size='small' variant='outlined' sx={{ fontSize: '0.65rem', mr: 0.5 }} />
                            )) : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={room.status}
                              color={statusColors[room.status] || 'default'}
                              size='small'
                              sx={{ fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{room.stage}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{room.playerCount}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: t.primaryText }}>
                              ${fmt(room.totalWageredUsd)}
                            </MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.75rem', color: (room.netPnlUsd || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                              {(room.netPnlUsd || 0) >= 0 ? '+' : ''}${fmt(room.netPnlUsd)}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtBidTime(room.lastBetTimestamp)}</MDTypography>
                          </TableCell>
                          <TableCell align='center'>
                            <MDButton
                              variant='gradient'
                              color='info'
                              size='small'
                              sx={{ borderRadius: '8px', fontSize: '0.7rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/nh-room/${room.roomId}`);
                              }}
                            >
                              Dashboard
                            </MDButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NHRoomList;
